/**
 * Pure immutable tree helpers for the WorkflowStep model.
 * Every function returns new objects; nothing is mutated.
 */
import type {
  ActionType,
  ConditionGroup,
  RecipientPolicy,
  RuleAction,
  RulePackDomain,
  RulePackDraft,
  WorkflowBranch,
  WorkflowStep,
} from '../../types';
import { defaultActionTypeForDomain } from '../../types';
import { makeEditorId, rungsToWorkflowSteps, workflowStepsToRungs } from '../../utils';

// ---------------------------------------------------------------------------
// Read helpers
// ---------------------------------------------------------------------------

export function getWorkflowSteps(draft: RulePackDraft): WorkflowStep[] {
  if (draft.steps && draft.steps.length > 0) return draft.steps;
  return rungsToWorkflowSteps(draft.rungs);
}

export function setWorkflowSteps(steps: WorkflowStep[]): Partial<RulePackDraft> {
  return { steps, rungs: workflowStepsToRungs(steps) };
}

// ---------------------------------------------------------------------------
// Recursive search
// ---------------------------------------------------------------------------

export function findBranchById(
  steps: WorkflowStep[],
  branchId: string,
): WorkflowBranch | undefined {
  for (const step of steps) {
    if (step.kind === 'branch') {
      for (const branch of step.branches) {
        if (branch.id === branchId) return branch;
        const nested = findBranchById(branch.steps, branchId);
        if (nested) return nested;
      }
      const inElse = findBranchById(step.elseSteps, branchId);
      if (inElse) return inElse;
    }
  }
  return undefined;
}

export function findActionById(
  steps: WorkflowStep[],
  actionId: string,
): RuleAction | undefined {
  for (const step of steps) {
    if (step.kind === 'action' && step.action.id === actionId) {
      return step.action;
    }
    if (step.kind === 'branch') {
      for (const branch of step.branches) {
        const found = findActionById(branch.steps, actionId);
        if (found) return found;
      }
      const foundElse = findActionById(step.elseSteps, actionId);
      if (foundElse) return foundElse;
    }
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Recursive update
// ---------------------------------------------------------------------------

export function updateBranchById(
  steps: WorkflowStep[],
  branchId: string,
  partial: Partial<WorkflowBranch>,
): WorkflowStep[] {
  return steps.map((step) => {
    if (step.kind !== 'branch') return step;
    return {
      ...step,
      branches: step.branches.map((b) => {
        if (b.id === branchId) return { ...b, ...partial };
        return { ...b, steps: updateBranchById(b.steps, branchId, partial) };
      }),
      elseSteps: updateBranchById(step.elseSteps, branchId, partial),
    };
  });
}

export function updateActionById(
  steps: WorkflowStep[],
  actionId: string,
  partial: Partial<RuleAction>,
): WorkflowStep[] {
  return steps.map((step) => {
    if (step.kind === 'action' && step.action.id === actionId) {
      return { ...step, action: { ...step.action, ...partial } };
    }
    if (step.kind === 'branch') {
      return {
        ...step,
        branches: step.branches.map((b) => ({
          ...b,
          steps: updateActionById(b.steps, actionId, partial),
        })),
        elseSteps: updateActionById(step.elseSteps, actionId, partial),
      };
    }
    return step;
  });
}

// ---------------------------------------------------------------------------
// Insert / remove operations
// ---------------------------------------------------------------------------

export function deepInsertAfter(
  steps: WorkflowStep[],
  afterId: string,
  newStep: WorkflowStep,
): WorkflowStep[] {
  const result: WorkflowStep[] = [];
  for (const step of steps) {
    if (step.kind === 'branch') {
      result.push({
        ...step,
        branches: step.branches.map((b) => ({
          ...b,
          steps: deepInsertAfter(b.steps, afterId, newStep),
        })),
        elseSteps: deepInsertAfter(step.elseSteps, afterId, newStep),
      });
    } else {
      result.push(step);
    }
    if (step.id === afterId) {
      result.push(newStep);
    }
  }
  return result;
}

export function deepRemoveStep(
  steps: WorkflowStep[],
  stepId: string,
): WorkflowStep[] {
  return steps
    .filter((s) => s.id !== stepId)
    .map((step) => {
      if (step.kind === 'branch') {
        return {
          ...step,
          branches: step.branches.map((b) => ({
            ...b,
            steps: deepRemoveStep(b.steps, stepId),
          })),
          elseSteps: deepRemoveStep(step.elseSteps, stepId),
        };
      }
      return step;
    });
}

export function appendToBranch(
  steps: WorkflowStep[],
  branchId: string,
  newStep: WorkflowStep,
): WorkflowStep[] {
  return steps.map((step) => {
    if (step.kind !== 'branch') return step;
    return {
      ...step,
      branches: step.branches.map((b) => {
        if (b.id === branchId) return { ...b, steps: [...b.steps, newStep] };
        return { ...b, steps: appendToBranch(b.steps, branchId, newStep) };
      }),
      elseSteps: appendToBranch(step.elseSteps, branchId, newStep),
    };
  });
}

export function addSiblingBranch(
  steps: WorkflowStep[],
  splitId: string,
): WorkflowStep[] {
  return steps.map((step) => {
    if (step.kind === 'branch' && step.id === splitId) {
      const newBranch = createWorkflowBranch(step.branches.length);
      const updatedBranches = [...step.branches, newBranch].map((b, i) => ({
        ...b,
        priority: i,
      }));
      return { ...step, branches: updatedBranches };
    }
    if (step.kind === 'branch') {
      return {
        ...step,
        branches: step.branches.map((b) => ({
          ...b,
          steps: addSiblingBranch(b.steps, splitId),
        })),
        elseSteps: addSiblingBranch(step.elseSteps, splitId),
      };
    }
    return step;
  });
}

export function removeBranch(
  steps: WorkflowStep[],
  splitId: string,
  branchId: string,
): WorkflowStep[] {
  return steps.map((step) => {
    if (step.kind === 'branch' && step.id === splitId) {
      const filtered = step.branches.filter((b) => b.id !== branchId);
      const reindexed = filtered.map((b, i) => ({ ...b, priority: i }));
      return { ...step, branches: reindexed };
    }
    if (step.kind === 'branch') {
      return {
        ...step,
        branches: step.branches.map((b) => ({
          ...b,
          steps: removeBranch(b.steps, splitId, branchId),
        })),
        elseSteps: removeBranch(step.elseSteps, splitId, branchId),
      };
    }
    return step;
  });
}

/**
 * Smart branch removal: if the split has only one IF branch left and it's being
 * removed, remove the entire branch-split step (including Else). Otherwise
 * just remove the branch and reindex.
 */
export function removeBranchOrSplit(
  steps: WorkflowStep[],
  splitId: string,
  branchId: string,
): WorkflowStep[] {
  return steps
    .flatMap((step) => {
      if (step.kind === 'branch' && step.id === splitId) {
        if (step.branches.length <= 1) {
          return [];
        }
        const filtered = step.branches.filter((b) => b.id !== branchId);
        const reindexed = filtered.map((b, i) => ({ ...b, priority: i }));
        return [{ ...step, branches: reindexed } as WorkflowStep];
      }
      if (step.kind === 'branch') {
        return [{
          ...step,
          branches: step.branches.map((b) => ({
            ...b,
            steps: removeBranchOrSplit(b.steps, splitId, branchId),
          })),
          elseSteps: removeBranchOrSplit(step.elseSteps, splitId, branchId),
        } as WorkflowStep];
      }
      return [step];
    });
}

export function appendToElse(
  steps: WorkflowStep[],
  splitId: string,
  newStep: WorkflowStep,
): WorkflowStep[] {
  return steps.map((step) => {
    if (step.kind === 'branch' && step.id === splitId) {
      return { ...step, elseSteps: [...step.elseSteps, newStep] };
    }
    if (step.kind === 'branch') {
      return {
        ...step,
        branches: step.branches.map((b) => ({
          ...b,
          steps: appendToElse(b.steps, splitId, newStep),
        })),
        elseSteps: appendToElse(step.elseSteps, splitId, newStep),
      };
    }
    return step;
  });
}

// ---------------------------------------------------------------------------
// Factory helpers
// ---------------------------------------------------------------------------

export function defaultConditionGroup(): ConditionGroup {
  return {
    id: makeEditorId('cg'),
    groupOperator: 'AND',
    conditions: [
      {
        id: makeEditorId('c'),
        field: 'patient.active' as ConditionGroup['conditions'][0]['field'],
        operator: 'equals',
        value: '',
      },
    ],
  };
}

export function createWorkflowBranch(index: number): WorkflowBranch {
  return {
    id: makeEditorId('branch'),
    name: `Branch ${index + 1}`,
    enabled: true,
    alwaysRun: false,
    conditionGroups: [defaultConditionGroup()],
    steps: [],
    priority: index,
  };
}

export function createBranchSplitStep(): WorkflowStep {
  return {
    id: makeEditorId('split'),
    kind: 'branch',
    branches: [createWorkflowBranch(0)],
    elseSteps: [],
  };
}

const DEFAULT_CLIENT_PRIMARY: RecipientPolicy[] = ['client-primary'];

function defaultRecipientPoliciesForActionType(
  actionType: ActionType,
): Pick<
  RuleAction,
  'notificationRecipientPolicies' | 'emailRecipientPolicies' | 'assignmentRecipientPolicies'
> {
  switch (actionType) {
    case 'send-notification':
      return { notificationRecipientPolicies: DEFAULT_CLIENT_PRIMARY };
    case 'send-email':
      return { emailRecipientPolicies: DEFAULT_CLIENT_PRIMARY };
    case 'assign-template-forms':
      return { assignmentRecipientPolicies: DEFAULT_CLIENT_PRIMARY };
    case 'set-claim-field':
      return {};
  }
}

/** Apply action type change and persist default recipient policies for the new type. */
export function applyActionTypeChange(
  _action: RuleAction,
  newType: ActionType,
): Partial<RuleAction> {
  const recipientDefaults = defaultRecipientPoliciesForActionType(newType);
  return {
    type: newType,
    notificationRecipientPolicies:
      newType === 'send-notification' ? recipientDefaults.notificationRecipientPolicies : undefined,
    emailRecipientPolicies:
      newType === 'send-email' ? recipientDefaults.emailRecipientPolicies : undefined,
    assignmentRecipientPolicies:
      newType === 'assign-template-forms' ? recipientDefaults.assignmentRecipientPolicies : undefined,
    claimFieldPath: undefined,
    claimFieldValue: undefined,
    claimFieldSystem: undefined,
    claimFieldValueSource: undefined,
    ...(newType === 'set-claim-field'
      ? { timingBucket: 'immediate' as const, scheduledTiming: undefined }
      : {}),
  };
}

export function createDefaultAction(
  actionType: ActionType = 'send-notification',
): RuleAction {
  return {
    id: makeEditorId('act'),
    type: actionType,
    label: '',
    ...defaultRecipientPoliciesForActionType(actionType),
    ...(actionType === 'set-claim-field'
      ? { timingBucket: 'immediate' as const }
      : {}),
  };
}

export function createActionStep(
  actionType: ActionType = 'send-notification',
): WorkflowStep {
  return {
    id: makeEditorId('ws'),
    kind: 'action',
    action: createDefaultAction(actionType),
  };
}

/** Payload for empty-canvas "Add actions": one always-run rung with the domain default action. */
export function createInitialAlwaysRunUpdate(domain: RulePackDomain): Partial<RulePackDraft> {
  return {
    rungs: [
      {
        id: makeEditorId('rung'),
        name: '',
        enabled: true,
        alwaysRun: true,
        conditionGroups: [],
        actions: [createDefaultAction(defaultActionTypeForDomain(domain))],
        priority: 0,
      },
    ],
    steps: undefined,
  };
}
